import React from 'react';
import { motion } from 'framer-motion';
import { 
  EnvelopeIcon, 
  MapPinIcon,
  GlobeAltIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useInView } from 'react-intersection-observer';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const contactInfo = [
    {
      icon: EnvelopeIcon,
      title: 'Email',
      details: 'dhanish@elementzonline.com',
      subtitle: 'Response within 1 hour',
      href: 'mailto:dhanish@elementzonline.com'
    },
    {
      icon: MapPinIcon,
      title: 'Location',
      details: 'Kerala, India',
      subtitle: 'Serving clients globally',
      href: null
    },
    {
      icon: GlobeAltIcon,
      title: 'Fiverr Profile',
      details: 'fiverr.com/dhanishvijayan',
      subtitle: '500+ Five-star reviews',
      href: 'https://fiverr.com/dhanishvijayan'
    },
  ];

  const services = [
    'IoT Hardware-Software Integration',
    'AWS Cloud Architecture & Deployment',
    'Python Development (Django/FastAPI)',
    'System Architecture & Design',
    'API Development & Integration',
    'Database Design & Optimization',
    'DevOps & CI/CD Implementation',
    'Technical Consulting & Support'
  ];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section id="contact" className="section-padding bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto container-padding">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Let's Build Something <span className="gradient-text">Amazing Together</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Ready to transform your ideas into reality? I'm here to help you succeed 
              with cutting-edge technology solutions tailored to your business needs.
            </p>
          </motion.div>

          {/* Contact Information */}
          <div className="max-w-4xl mx-auto">
            <motion.div variants={itemVariants}>
              <div className="mb-8">
                {/* Professional Image Header */}
                <motion.div
                  className="flex flex-col items-center mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="relative mb-4">
                    <div className="w-24 h-24 rounded-full overflow-hidden shadow-lg border-4 border-primary-200 dark:border-primary-800">
                      <img
                        src="/images/dhanish.jpeg"
                        alt="Dhanish Vijayan - Chief Technology Officer"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white dark:border-gray-900"></div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                    Get in Touch with Dhanish
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-center">
                    Ready to discuss your project? Let's connect!
                  </p>
                </motion.div>
                
                {/* Contact Methods */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  {contactInfo.map((contact, index) => (
                    <motion.div
                      key={index}
                      className="flex flex-col items-center text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                      whileHover={{ scale: 1.02 }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                    >
                      <contact.icon className="h-8 w-8 text-primary-600 dark:text-primary-400 mb-4" />
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                        {contact.title}
                      </h4>
                      {contact.href ? (
                        <a 
                          href={contact.href}
                          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium mb-2"
                        >
                          {contact.details}
                        </a>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-300 font-medium mb-2">
                          {contact.details}
                        </p>
                      )}
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {contact.subtitle}
                      </p>
                    </motion.div>
                  ))}
                </div>

                {/* Availability */}
                <motion.div
                  className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 p-6 rounded-xl border border-primary-100 dark:border-primary-800 mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <div className="flex items-center justify-center mb-3">
                    <ClockIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      Availability
                    </h4>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-2 text-center">
                    Available 24/7 for urgent projects
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                    Regular hours: 9 AM - 9 PM IST (Monday - Sunday)
                  </p>
                </motion.div>

                {/* Services List */}
                <motion.div
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                >
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-6">
                    Services I Offer:
                  </h4>
                  <div className="grid md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                    {services.map((service, index) => (
                      <div key={index} className="flex items-center justify-start">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-600 dark:text-gray-300">
                          {service}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            variants={itemVariants}
            className="mt-16 text-center bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-8 text-white"
          >
            <h3 className="text-2xl font-bold mb-4">
              Prefer a Direct Conversation?
            </h3>
            <p className="text-lg mb-6 text-primary-100">
              Book a free 15-minute consultation call to discuss your project in detail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="mailto:dhanish@elementzonline.com"
                className="bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 inline-flex items-center justify-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <EnvelopeIcon className="h-5 w-5 mr-2" />
                Email Me Directly
              </motion.a>
              <motion.a
                href="https://fiverr.com/dhanishvijayan"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors duration-200 inline-flex items-center justify-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <GlobeAltIcon className="h-5 w-5 mr-2" />
                View Fiverr Profile
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;